import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from 'rxjs';
import { ProductionPauseResponseDto } from 'src/app/shared/dto/pagination/production-pause-response.dto';
import { ProductionPauseDto } from 'src/app/shared/dto/production-pause.dto';
import { SetValidationParams } from 'src/app/shared/dto/set-validation-params.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { formatClientRoom, Client, RoomNames, formatClientRoomProcessId } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class PauseService extends BaseService {

    public pauses$: Observable<ProductionPauseResponse>;
    public navbarCounter$: Observable<number>;
    public pausesByProcessId$: Observable<ProductionPauseDto[]>;
    
    private pausesSubject = new BehaviorSubject<ProductionPauseResponse>(new ProductionPauseResponse);
    private messageSubscription: Subscription | undefined;

    private pageNumber: number = 1;
    private pageSize: number = 20;
    private filterStatus: string = '';
    private filterText: string = '';
    private filterPausedByIds: string = '';
    private filterResumedByIds: string = '';
    private filterValidatedByIds: string = '';
    private filterAcceptedValues: string = '';
    private fromDate: Date | null = null;
    private toDate: Date | null = null;
    private sortByStartDate: string = 'ascend';

    private processId!: number;

    private pausesByProcessIdSubject = new BehaviorSubject<ProductionPauseDto[]>([]);
    private messageSubscriptionPausesByProcessId: Subscription | undefined;

    private navbarCounterSubject = new BehaviorSubject<number>(0);
    private messageSubscriptionNavbarCounter: Subscription | undefined;

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'pauses');

        this.pauses$ = this.pausesSubject.asObservable();
        this.navbarCounter$ = this.navbarCounterSubject.asObservable();
        this.pausesByProcessId$ = this.pausesByProcessIdSubject.asObservable();
    }

    refreshVariables(pageSize: number, pageNumber: number, filterStatus: string, filterText: string, filterPausedByIds: string, filterResumedByIds: string, filterValidatedByIds: string, filterAcceptedValues: string, fromDate: Date | null, toDate: Date | null, sortByStartDate: string): void {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;
        this.filterStatus = filterStatus;
        this.filterText = filterText;
        this.filterPausedByIds = filterPausedByIds;
        this.filterResumedByIds = filterResumedByIds;
        this.filterValidatedByIds = filterValidatedByIds;
        this.filterAcceptedValues = filterAcceptedValues;
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.sortByStartDate = sortByStartDate;
    }

    async setupWebSocket(pageSize: number, pageNumber: number, filterStatus: string): Promise<void> {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;
        this.filterStatus = filterStatus;

        try {
            const response = await this.getPaginatedPauses(this.pageSize, this.pageNumber, this.filterStatus, this.filterText, this.filterPausedByIds, this.filterResumedByIds, this.filterValidatedByIds, this.filterAcceptedValues, this.fromDate, this.toDate, this.sortByStartDate);
            this.pausesSubject.next({ productionPauses: response.productionPauses, totalProductionPauses: response.total });

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PAUSES_ROOM));
            this.messageSubscription = this.socketService.getNotifications(Messages.PAUSES_CHANGED).subscribe(async () => {
                const response = await this.getPaginatedPauses(this.pageSize, this.pageNumber, this.filterStatus, this.filterText, this.filterPausedByIds, this.filterResumedByIds, this.filterValidatedByIds, this.filterAcceptedValues, this.fromDate, this.toDate, this.sortByStartDate);
                this.pausesSubject.next({ productionPauses: response.productionPauses, totalProductionPauses: response.total });
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Pausas. Error: ${e.message}`);
        }
    }

    async setupWebSocketByProcessId(processId: number): Promise<void> {
        try {
            this.processId = processId;
            const response = await this.getByProcessId(this.processId);
            this.pausesByProcessIdSubject.next(response);

            this.socketService.conectSocketRoom(formatClientRoomProcessId(Client.DEFAULT_CLIENT, RoomNames.PAUSES_ROOM, this.processId));
            this.messageSubscriptionPausesByProcessId = this.socketService.getNotifications(Messages.PAUSES_CHANGED).subscribe(async () => {
                const response = await this.getByProcessId(this.processId);
                this.pausesByProcessIdSubject.next(response);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Pausas. Error: ${e.message}`);
        }
    }

    async setupWebSocketCounter(): Promise<void> {
        try {
            const response = await this.countNavBarBadge();
            this.navbarCounterSubject.next(response);

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PAUSES_ROOM));
            this.messageSubscriptionNavbarCounter = this.socketService.getNotifications(Messages.PAUSES_COUNTER_CHANGED).subscribe(async () => {
                const response = await this.countNavBarBadge();
                this.navbarCounterSubject.next(response);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Pausas. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        if(this.messageSubscription){
            this.messageSubscription.unsubscribe();
        }
    }

    disconnectWebSocketByProcessId(): void {
        if(this.messageSubscriptionPausesByProcessId){
            this.messageSubscriptionPausesByProcessId.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoomProcessId(Client.DEFAULT_CLIENT, RoomNames.PAUSES_ROOM, this.processId));
    }

    disconnectWebSocketCounter(): void {
        if(this.messageSubscriptionNavbarCounter){
            this.messageSubscriptionNavbarCounter.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PAUSES_ROOM));
    }

    async getPaginatedPauses(pageSize: number, pageNumber: number, filterStatus: string, filterText: string, filterPausedByIds: string, filterResumedByIds: string, filterValidatedByIds: string, filterAcceptedValues: string, fromDate: Date | null, toDate: Date | null, sortByStartDate: string): Promise<ProductionPauseResponseDto> {
        try {
            let url: string = `${this.url}/findAll?pageSize=${pageSize}&pageNumber=${pageNumber}&filterStatus=${filterStatus}`;

            if (sortByStartDate) {
                url = `${url}&sortByStartDate=${sortByStartDate}`;
            }

            if (filterText && filterText != '') {
                url = `${url}&filterText=${filterText}`;
            }

            if (filterPausedByIds && filterPausedByIds != '') {
                url = `${url}&filterPausedByIds=${filterPausedByIds}`;
            }

            if (filterResumedByIds && filterResumedByIds != '') {
                url = `${url}&filterResumedByIds=${filterResumedByIds}`;
            }

            if (filterValidatedByIds && filterValidatedByIds != '') {
                url = `${url}&filterValidatedByIds=${filterValidatedByIds}`;
            }

            if (filterAcceptedValues && filterAcceptedValues != '') {
                url = `${url}&filterAcceptedValues=${filterAcceptedValues}`;
            }

            if (fromDate) {
                url = `${url}&fromDate=${fromDate.toUTCString()}`;
            }

            if (toDate) {
                url = `${url}&toDate=${toDate.toUTCString()}`;
            }

            const request = this.httpClient.get<ProductionPauseResponseDto>(url);

            const productionPauseResponse: ProductionPauseResponseDto = await lastValueFrom(request);

            for (let i = 0; i < productionPauseResponse.productionPauses.length; i++) {
                if (productionPauseResponse.productionPauses[i].endDate) {
                    productionPauseResponse.productionPauses[i].endDate = new Date(productionPauseResponse.productionPauses[i].endDate.toString());
                }

                productionPauseResponse.productionPauses[i].startDate = new Date(productionPauseResponse.productionPauses[i].startDate.toString());
            }

            return productionPauseResponse;
        } catch (e: any) {
            throw new Error(`No se ha encontrado pausa. Error: ${e.message}`);
        }
    }

    async getByProcessId(processId: number): Promise<ProductionPauseDto[]> {
        try {
            if (!processId || processId <= 0) {
                throw Error('El proceso ha de ser mayor o igual que cero.');
            }

            const pausesObservable = this.httpClient.get<ProductionPauseDto[]>(
                `${this.url}/findByOrderId/${processId}`
            );

            const pauses = await lastValueFrom(pausesObservable);

            for (let i = 0; i < pauses.length; i++) {
                if (pauses[i].endDate) {
                    pauses[i].endDate = new Date(pauses[i].endDate.toString());
                }

                pauses[i].startDate = new Date(pauses[i].startDate.toString());
            }

            return pauses;
        } catch (e: any) {
            throw new Error(`No se han podido obtener las pausas del proceso ${processId ?? ''}. Error: ${e.message}`);
        }
    }

    async countNavBarBadge(): Promise<number> {
        try {
            const pausesObservable = this.httpClient.get<number>(
                `${this.url}/countNavBarBadge`
            );

            const countNavBarBadge = await lastValueFrom(pausesObservable);

            return countNavBarBadge;
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el contador de pausas. Error: ${e.message}`);
        }
    }

    async validateById(params: SetValidationParams): Promise<ProductionPauseDto> {
        try {
            const request = this.httpClient.patch<ProductionPauseDto>(`${this.url}/validateById/${params.pauseId}`, params);

            const productionPause: ProductionPauseDto = await lastValueFrom(request);

            return productionPause;
        } catch (error: any) {
            throw new Error(`No se ha podido validar la pausa. Error: ${error.message}`);
        }
    }

    async updatePause(productionPause: ProductionPauseDto): Promise<ProductionPauseDto> {
        try {
            const request = this.httpClient.put<ProductionPauseDto>(`${this.url}/update`, productionPause);

            const updatedProductionPause: ProductionPauseDto = await lastValueFrom(request);

            return updatedProductionPause;

        } catch (e: any) {
            throw new Error(`No se ha podido actualizar el tipo de formato. Error: ${e.message}`);
        }
    }
}

export class ProductionPauseResponse {
    productionPauses!: ProductionPauseDto[];
    totalProductionPauses!: number;
}
